import { SLOTS_APIS } from "@/api_framework/api_config";

import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import {
  IBookingCreationBody,
  IBookingCreationPrepaidPayment,
  IBookingCreationResponse,
} from "@/api_framework/api_modals/Bookings";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import AxiosCurlirize from "axios-curlirize";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";
import { HOME_SERVICE_ID } from "@/constants";

AxiosCurlirize(axios, () => {});
export type RotaCreationReturnType = {
  status: boolean;
  message: string;
};


const parseCoordinate = (value?: number | null): number | null => {
  if (value === null || value === undefined) return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isAtHomeBookingCreationPayload = (filterParams: IBookingCreationBody): boolean =>
  filterParams?.service_details?.service_type_id === HOME_SERVICE_ID;

const buildAtHomeBookingCreationPayload = (filterParams: IBookingCreationBody) => {
  return {
    slot_id: filterParams?.slot_details?.slot_id,
    configuration_id: filterParams?.configuration_id ?? null,
    customer_id: filterParams?.user_details?.id,
    pet_id: filterParams?.pet_details?.id,
    staff_id: filterParams?.staff_details?.id ?? null,
    latitude: parseCoordinate(filterParams?.latitude),
    longitude: parseCoordinate(filterParams?.longitude),
    address: filterParams?.address,
    address_id: filterParams?.address_id,
    comments: filterParams?.comments ?? "",
    reason: filterParams?.reason ?? "",
    last_visited_selected: Boolean(filterParams?.last_visited_selected),
    cart_details: {
      cart_items: filterParams?.cart_details?.cart_items ?? [],
    },
  };
};

export const useBookingCreationAPI = () => {
  const dispatch = useAppDispatch();
  const [newBookingId, setNewBookingId] = useState<string | null>();
  const [prepaidPayment, setPrepaidPayment] = useState<IBookingCreationPrepaidPayment | null>(null);
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const execute = useCallback(
    async (filterParams: IBookingCreationBody) => {
      dispatch(
        setLoading({
          key: "isBookingCreationInProgress",
          status: true,
        }),
      );
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const isAtHomeBooking = isAtHomeBookingCreationPayload(filterParams);
        const isOverbooking = filterParams?.booking_type === "over_booking";
        const isAtHomeOverbooking = isAtHomeBooking && isOverbooking;
        const endpoint = isAtHomeOverbooking
          ? SLOTS_APIS?.BOOKING_CREATION?.baseURL
          : isAtHomeBooking
            ? SLOTS_APIS?.AT_HOME_BOOKING_CREATION?.baseURL
            : SLOTS_APIS?.BOOKING_CREATION?.baseURL;
        const payload = (isAtHomeBooking && !isOverbooking)
          ? buildAtHomeBookingCreationPayload(filterParams)
          : filterParams;
        const response: RotaCreationReturnType = await axios
          .post(endpoint ?? "", payload, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IBookingCreationResponse>) => {
            if (res.data.status === true) {
              setNewBookingId(res.data.data.booking_id);
              setPrepaidPayment(res.data.data?.prepaid_payment ?? null);

              dispatch(
                setLoading({
                  key: "isBookingCreationInProgress",
                  status: false,
                }),
              );
              return { status: true, message: "Booking_created" };
            } else {
              setPrepaidPayment(null);
              dispatch(
                setLoading({
                  key: "isBookingCreationInProgress",
                  status: false,
                }),
              );
            }
          })
          .catch((e: AxiosError) => {
            sendCurl(e.config["curlCommand"]);
            setPrepaidPayment(null);
            dispatch(
              setLoading({
                key: "isBookingCreationInProgress",
                status: false,
              }),
            );
            captureError(e);

            if (e.response.status === 500) {
              /* Internal Server Error */
              return { status: false, message: "Booking_created" };
            }
            if (e.response.status === 400 || e.code === "ERR_BAD_REQUEST") {
              return { status: false, message: "Booking_created" };
            }
          });
        return response;
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
      }
    },
    [dispatch, configuration, captureError, sendCurl],
  );
  return { newBookingId, prepaidPayment, execute };
};
